import { useMemo } from 'react';
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts';
import { CountriesEntity } from '~/hooks/api/insights/useGetIGInsights';

import { ChartContainer } from '../chart';

export interface CountryChartTheme {
  primary?: string;
  background?: string;
  backgroundAlpha?: string;
  textBase?: string;
}

const DEFAULT_COLORS = {
  primary: '#155FEF',
  background: '#ffffff',
  backgroundAlpha: '#dee2e8',
  textBase: '#15191e',
};

const CHART_HEIGHT = 212;
const COUNTRY_AXIS_WIDTH = 118;

interface CountryChartProps {
  countries?: CountriesEntity[] | null;
  theme?: CountryChartTheme;
}

export const CountryChart = ({
  countries: countriesData,
  theme,
}: CountryChartProps) => {
  const colors = {
    primary: theme?.primary ?? DEFAULT_COLORS.primary,
    background: theme?.background ?? DEFAULT_COLORS.background,
    backgroundAlpha: theme?.backgroundAlpha ?? DEFAULT_COLORS.backgroundAlpha,
    textBase: theme?.textBase ?? DEFAULT_COLORS.textBase,
  };

  const countries = useMemo(() => {
    if (!countriesData) return [];

    const regularCountries = countriesData.filter(
      (c: { label: string }) => c.label !== 'Others',
    );

    const sorted = regularCountries.sort(
      (c1: { value: number }, c2: { value: number }) => {
        if (c1.value < c2.value) return 1;
        if (c1.value > c2.value) return -1;
        return 0;
      },
    );

    const top5 = sorted.slice(0, 5);

    const result = top5.map(
      (c: { meta: { countryName: any }; label: any; value: number }) => ({
        country: c.meta?.countryName || c.label,
        value: Math.round(c.value * 100),
      }),
    );

    const topSum = result.reduce((sum, c) => sum + c.value, 0);
    const othersValue = 100 - topSum;

    if (othersValue > 0) {
      result.push({ country: 'Other', value: othersValue });
    }

    return result;
  }, [countriesData]);

  const chartData = useMemo(() => {
    return countries.map((country: { country: string; value: number }) => ({
      country: country.country,
      percentage: country.value,
    }));
  }, [countries]);

  const barSize = useMemo(() => {
    const itemCount = chartData.length;
    if (itemCount <= 0) return 15;
    const baseHeight = 180;
    const heightBonus = itemCount <= 3 ? 70 : itemCount <= 4 ? 30 : 0;
    const availableHeight = baseHeight + heightBonus;
    const maxBarSize = 55;
    const minBarSize = 15;
    const calculatedSize = Math.floor((availableHeight * 0.7) / itemCount);
    return Math.max(minBarSize, Math.min(maxBarSize, calculatedSize));
  }, [chartData.length]);

  return (
    <div
      className="flex h-[280px] min-w-[280px] flex-[1_1_320px] flex-col gap-4 rounded-12 border p-4"
      style={{
        backgroundColor: colors.background,
        borderColor: colors.backgroundAlpha,
      }}
    >
      <p className="text-16 font-500" style={{ color: colors.textBase }}>
        Country distribution
      </p>
      <ChartContainer
        config={{
          percentage: {
            label: 'Percentage',
            color: colors.primary,
          },
        }}
        className="h-[212px] w-full outline-none [&_*]:outline-none"
      >
        <BarChart
          accessibilityLayer
          data={chartData}
          layout="vertical"
          width={318}
          height={CHART_HEIGHT}
          margin={{
            right: 40,
          }}
        >
          <XAxis type="number" dataKey="percentage" hide />
          <YAxis
            dataKey="country"
            type="category"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            width={COUNTRY_AXIS_WIDTH}
            tick={{ fill: colors.textBase }}
          />
          <Bar
            dataKey="percentage"
            fill={colors.primary}
            radius={5}
            barSize={barSize}
          >
            <LabelList
              dataKey="percentage"
              position="right"
              offset={8}
              className="text-12 font-400"
              fill={colors.textBase}
              formatter={(value: number) => `${value}%`}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};
